import { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { API_URL } from "../../../config";
import { AuthContext } from "../../../Context/AuthContext";
import axiosInstance from "../../../utils/axiosInstance";
import "./TraineeDashboard.css";

const canDo = (user, module, action) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.[action] === true;
};

const canViewPage = (user, module) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.view === true;
};

function Trainee() {
  const [employee, setEmployee] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/getTraineeData`)
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setEmployee(res.data);
        } else {
          setEmployee([]);
        }
      })
      .catch(() => setEmployee([]));
  }, []);

  const handleDelete = async (employeeId) => {
    if (!canDo(user, "trainees", "delete")) return alert("No permission");

    if (!window.confirm("Delete trainee?")) return;

    try {
      await axiosInstance.delete(`${API_URL}/api/deleteSignUpUser/${employeeId}`);
      setEmployee((prev) => prev.filter((emp) => emp.employeeId !== employeeId));
    } catch {
      alert("Delete failed");
    }
  };

  const handleToggleBlock = async (emp) => {
    const isCurrentlyBlocked = emp?.isActive === false;
    const nextIsActive = isCurrentlyBlocked; // blocked -> true (unblock), active -> false (block)
    const actionText = isCurrentlyBlocked ? "unblock" : "block";

    if (!window.confirm(`Do you want to ${actionText} this trainee/intern?`)) return;

    try {
      const targetId = emp?.employeeId || emp?._id;
      const res = await axiosInstance.put(`${API_URL}/api/toggleEmployeeBlock/${targetId}`, {
        isActive: nextIsActive,
      });
      const updated = res?.data?.user;
      if (updated) {
        setEmployee((prev) =>
          prev.map((row) => (String(row._id) === String(updated._id) ? { ...row, ...updated } : row))
        );
      }
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to update trainee/intern status");
    }
  };

  if (!canViewPage(user, "trainees")) {
    return <div className="text-danger text-center mt-5">Access Denied</div>;
  }

  const filtered = employee.filter((emp) => {
    const q = search.toLowerCase();
    return (
      emp.employeeId?.toLowerCase().includes(q) ||
      emp.ename?.toLowerCase().includes(q) ||
      emp.phoneNo?.toLowerCase().includes(q) ||
      emp.official_email?.toLowerCase().includes(q) ||
      emp.department?.deptName?.toLowerCase().includes(q) ||
      emp.service?.serviceName?.toLowerCase().includes(q)
    );
  });

  const exportToExcel = () => {
    const exportRows = filtered.map((emp) => ({
      ID: emp.employeeId || "",
      Name: emp.ename || "",
      Phone: emp.phoneNo || "",
      Email: emp.official_email || "",
      Exp: emp.lastExp || "",
      Dept: emp.department?.deptName || "",
      Service: emp.service?.serviceName || "",
      Type: emp.userType || "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Trainees");
    XLSX.writeFile(wb, "Trainee_Intern_List.xlsx");
  };

  return (
    <div className="trdash-page">
      <div className="trdash-shell">
        <div className="trdash-hero">
          <div className="trdash-hero-top">
            <div>
              <p className="trdash-eyebrow">Talent Pipeline</p>
              <h3>Trainee & Intern List</h3>
            </div>
            <button type="button" className="trdash-add-btn trdash-hero-export" onClick={exportToExcel}>
              Export
            </button>
          </div>
        </div>

        <div className="trdash-toolbar">
          {canDo(user, "trainees", "add") && (
            <Link to="/admin/addemployee" className="trdash-add-btn">
              Add Trainee/Intern
            </Link>
          )}

          <input
            type="text"
            placeholder="Search trainee/intern..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="trdash-search"
          />
        </div>

        <div className="trdash-table-card">
          <div className="table-responsive">
            <table className="table align-middle mb-0 text-center">
              <thead>
                <tr>
                  {["ID", "Name", "Phone", "Email", "Exp", "Dept", "Service", "Type", "Lock Status", "Action"].map(
                    (h, i) => (
                      <th key={i}>{h}</th>
                    )
                  )}
                </tr>
              </thead>

              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="9" className="trdash-empty-cell">
                      No trainee found
                    </td>
                  </tr>
                )}

                {filtered.map((emp) => (
                  <tr key={emp._id || emp.employeeId}>
                    <td>
                      <button
                        type="button"
                        className="trdash-id-link"
                        onClick={() => navigate(`/admin/viewTrainee/${emp.employeeId}`)}
                      >
                        {emp.employeeId}
                      </button>
                    </td>
                    <td>{emp.ename}</td>
                    <td>{emp.phoneNo}</td>
                    <td>{emp.official_email}</td>
                    <td>{emp.lastExp}</td>
                    <td>{emp.department?.deptName}</td>
                    <td>{emp.service?.serviceName}</td>

                    <td>
                      <span className={`trdash-badge ${emp.userType === "trainee" ? "trainee" : "intern"}`}>
                        {emp.userType}
                      </span>
                    </td>

                    <td>
                      {emp.isLocked ? (
                        <span className="badge bg-danger">Locked 🔒</span>
                      ) : (
                        <span className="badge bg-success">Active ✅</span>
                      )}
                    </td>

                    <td style={{ whiteSpace: "nowrap" }}>
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          className="btn btn-sm trdash-act view"
                          onClick={() => navigate(`/admin/viewTrainee/${emp.employeeId}`)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm trdash-act edit"
                          onClick={() => navigate(`/admin/upDateUder/${emp.employeeId}`)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm trdash-act move"
                          onClick={() => navigate(`/admin/moveToEmplyee/${emp.employeeId}`)}
                        >
                          Move
                        </button>
                        <button
                          className="btn btn-sm trdash-act delete"
                          onClick={() => handleDelete(emp.employeeId)}
                        >
                          Delete
                        </button>
                        <button
                          className={`btn btn-sm trdash-act ${emp?.isActive === false ? "unblock" : "block"}`}
                          onClick={() => handleToggleBlock(emp)}
                        >
                          {emp?.isActive === false ? "Unblock" : "Block"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Trainee;
